// apps/web/src/pages/Login.tsx
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { trpc } from "@/trpc/client";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { LoginSchema, TLoginSchema } from "@/lib/validators/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Mail, Lock, Loader2, Eye, EyeOff, Shield, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";


const isFakeEmail = (email: string): boolean => {
  const fakeEmailPatterns = [
    /example\.(com|org|net)/i,
    /test@/i,
    /fake@/i,
    /dummy@/i,
    /sample@/i,
    /@example/i,
    /@test\./i,
    /@fake\./i,
    /tempmail/i,
    /throwaway/i,
    /mailinator/i,
    /guerrillamail/i,
  ];
  return fakeEmailPatterns.some(pattern => pattern.test(email));
};


const detectSuspiciousActivity = (email: string, password: string): string | null => {

  const sqlPatterns = [
    /(\bOR\b|\bAND\b).*[=<>]/i,
    /['";].*(-{2}|\/\*|\*\/)/i,
    /UNION.*SELECT/i,
    /DROP.*TABLE/i,
    /INSERT.*INTO/i,
    /DELETE.*FROM/i,
  ];

  if (sqlPatterns.some(pattern => pattern.test(email) || pattern.test(password))) {
    return "Invalid credentials format detected. Security alert.";
  }

  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onload=/i,
    /<iframe/i,
  ];

  if (xssPatterns.some(pattern => pattern.test(email) || pattern.test(password))) {
    return "Invalid credentials format detected. Security alert.";
  }

  if (email.includes('../') || password.includes('../')) {
    return "Invalid credentials format detected. Security alert.";
  }

  return null;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setToken } = useAuthStore();

  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const form = useForm<TLoginSchema>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: "", password: "" }
  });

  // Handle lockout timer
  useEffect(() => {
    if (lockoutTimer > 0) {
      const timer = setTimeout(() => {
        setLockoutTimer(lockoutTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (lockoutTimer === 0 && isLocked) {
      setIsLocked(false);
      setLoginAttempts(0);
      toast.info("You can try logging in again");
    }
  }, [lockoutTimer, isLocked]);

  const { mutate, isLoading } = trpc.user.login.useMutation({
    onSuccess: (data) => {
      // Reset failed attempts on successful login
      setLoginAttempts(0);
      setIsLocked(false);
      setIsSubmitting(false);


      setUser(data.user);
      setToken(data.token);

      toast.success("Logged in successfully!");


      const status = data.user.verificationStatus;
      const onboardingStatus = data.user.onboardingStatus;

      if (status === 'UNVERIFIED' || (status === 'REJECTED' && onboardingStatus === 'pending_documents')) {
        navigate('/onboarding/verify', { replace: true });
      } else if (status === 'PENDING' || onboardingStatus === 'pending_admin_approval') {
        navigate('/onboarding/pending', { replace: true });
      } else {
        const from = location.state?.from?.pathname ||
          (data.user.role === 'ADMIN' ? '/admin/dashboard' : '/dashboard');
        navigate(from, { replace: true });
      }
    },
    onError: (error) => {
      setIsSubmitting(false);


      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      if (newAttempts >= 5) {
        setIsLocked(true);
        setLockoutTimer(300);
        toast.error("Too many failed attempts. Account locked for 5 minutes.");
      } else {
        toast.error(error.message);
        if (newAttempts >= 3) {
          toast.warning(`${5 - newAttempts} attempts remaining before lockout`);
        }
      }
    },
  });

  const onSubmit = useCallback((data: TLoginSchema) => {
    if (isLocked) {
      const minutes = Math.floor(lockoutTimer / 60);
      const seconds = lockoutTimer % 60;
      toast.error(`Account temporarily locked. Try again in ${minutes}:${seconds.toString().padStart(2, '0')}`);
      return;
    }

    if (isFakeEmail(data.email)) {
      toast.error("Please use a valid email address");
      form.setError("email", {
        type: "manual",
        message: "Invalid or temporary email address"
      });
      return;
    }

    const suspiciousActivity = detectSuspiciousActivity(data.email, data.password);
    if (suspiciousActivity) {
      toast.error(suspiciousActivity);
      setLoginAttempts(prev => prev + 1);
      return;
    }

    const sanitizedData = {
      email: data.email.trim().toLowerCase(),
      password: data.password,
    };

    setIsSubmitting(true);
    mutate(sanitizedData);
  }, [isLocked, lockoutTimer, form, mutate, loginAttempts]);



  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-6 sm:py-12 animate-fade-in">
      <Card className="w-full max-w-md glass">
        {/* ... existing header ... */}
        <CardHeader className="text-center space-y-2 p-4 sm:p-6">
          {/* ... titile ... */}
          <CardTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            Welcome Back!
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">Enter your credentials to access your account.</CardDescription>
        </CardHeader>
        <CardContent>
          {/* ... existing alerts ... */}

          <Form {...form}>
            {/* ... form ... */}
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* ... fields ... */}

              {/* ... existing fields ... */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <Label>Email</Label>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="youremail@domain.com"
                          className="pl-10"
                          {...field}
                          disabled={isLoading || isLocked}
                          autoComplete="email"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <Label>Password</Label>
                      <Link
                        to="/forgot-password"
                        className="text-xs text-muted-foreground hover:text-primary"
                        tabIndex={-1}
                      >
                        Forgot?
                      </Link>
                    </div>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={showPassword ? "text" : "password"}
                          className="pl-10 pr-10"
                          {...field}
                          disabled={isLoading || isLocked}
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          disabled={isLoading || isLocked}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />


              <Button
                type="submit"
                variant="default"
                className={`w-full bg-primary hover:bg-primary/90 transition-all duration-200 ${isLoading || isLocked || isSubmitting ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                disabled={isLoading || isLocked || isSubmitting}
              >
                {isLoading || isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : isLocked ? (
                  "Account Locked"
                ) : (
                  "Log In"
                )}
              </Button>

              <div className="mt-6 text-center text-sm">
                Don't have an account?{" "}
                <Link to="/register" className="font-semibold text-primary hover:underline">
                  Sign up
                </Link>
              </div>
            </form>
          </Form>

          {/* Security Information */}
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Shield className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <p>
                Your connection is secure. We use industry-standard encryption to protect your credentials.
              </p>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default Login;